import React from 'react';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';
import { Field, SearchField, SelectField } from './field';
import { pad2, plural } from './gallery';

/**
 * Testemunhos — rota `/testimonials`, dentro do layout com sidebar (item "Testemunhos", ícone
 * `message-square-quote`, grupo **Conteúdo** logo abaixo de "Galeria"). Prints: 18–22 (skill
 * `aliste-se-admin-kit`). O topo é o `AppTopbar`; no celular a pílula vira o `TopbarMoreMenu` da galeria.
 *
 * Tela de CURADORIA: o painel não cria nem edita texto. Três ações por testemunho, e só elas:
 * Mostrar no início (HomeToggle) · Anexar/Trocar/Remover vídeo · Excluir.
 *
 * Reuso no site público: `TestimonialCard` é só exibição (texto, pessoa, acampamento, vídeo). Tudo que é
 * curadoria entra pelos slots `badges`, `selection` e `actions` — no site, basta não passar nenhum deles.
 *
 * Teclado: Esc fecha na ordem dialog → player → seleção; Enter confirma o dialog aberto.
 * Animações: `anim-ci-in` (dialogs, barra, toast), `anim-gl-fade` (player), `anim-tm-pulse` (skeleton).
 */

export const HOME_LIMIT = 6;

export type TestimonialVideo = {
  kind: 'file' | 'youtube' | 'instagram';
  /** Arquivo: URL do MP4/MOV. Link: URL colada (YouTube/Instagram). */
  url: string;
  posterUrl?: string | null;
  /** Segundos. Opcional para links. */
  duration?: number | null;
};

export type Testimonial = {
  id: string;
  name: string;
  /** Nome do evento, ex.: "Aliste-se Forja" */
  camp: string;
  /** Ano do evento. */
  year: number | null;
  text: string;
  photoUrl?: string | null;
  showOnHome: boolean;
  video?: TestimonialVideo | null;
  createdAt: string | Date;
};

export const campLabel = (t: Pick<Testimonial, 'camp' | 'year'>) =>
  t.year ? `${t.camp} ${t.year}` : t.camp;

export const initials = (name: string) => {
  const p = name.trim().split(/\s+/);
  return (
    (p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
};

export const formatDuration = (s?: number | null) =>
  s == null ? '' : `${Math.floor(s / 60)}:${pad2(Math.round(s % 60))}`;

export const relativeTime = (d: string | Date, now = new Date()) => {
  const days = Math.round((now.getTime() - new Date(d).getTime()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  if (days < 30) {
    const w = Math.round(days / 7);
    return `há ${w} ${w === 1 ? 'semana' : 'semanas'}`;
  }
  if (days < 365) {
    const m = Math.round(days / 30);
    return `há ${m} ${m === 1 ? 'mês' : 'meses'}`;
  }
  const y = Math.round(days / 365);
  return `há ${y} ${y === 1 ? 'ano' : 'anos'}`;
};

export const normalize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export type ParsedLink =
  | { kind: 'youtube'; id: string; posterUrl: string }
  | { kind: 'instagram'; posterUrl: null }
  | { kind: 'invalid' };
export const parseVideoLink = (url: string): ParsedLink | null => {
  const u = url.trim();
  if (!u) return null;
  const yt = u.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/,
  )?.[1];
  if (yt)
    return {
      kind: 'youtube',
      id: yt,
      posterUrl: `https://img.youtube.com/vi/${yt}/hqdefault.jpg`,
    };
  if (/instagram\.com\/(reel|p|tv)\//.test(u))
    return { kind: 'instagram', posterUrl: null };
  return { kind: 'invalid' };
};

const SOURCE: Record<TestimonialVideo['kind'], string> = {
  file: 'Vídeo',
  youtube: 'YouTube',
  instagram: 'Instagram',
};

/* ─────────────── Exibição (reaproveitável no site) ─────────────── */

export const TestimonialAvatar: React.FC<{
  name: string;
  photoUrl?: string | null;
  className?: string;
}> = ({ name, photoUrl, className }) =>
  photoUrl ? (
    <img
      src={photoUrl}
      alt=""
      className={cn(
        'size-10 shrink-0 rounded-full border border-border object-cover',
        className,
      )}
    />
  ) : (
    <span
      className={cn(
        'font-display flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-sunken text-[13px] font-extrabold text-muted-foreground',
        className,
      )}
    >
      {initials(name)}
    </span>
  );

export const CampChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex h-[22px] items-center whitespace-nowrap rounded-md border border-border px-2 text-[10px] font-bold uppercase tracking-[.1em] text-muted-foreground">
    {children}
  </span>
);

const PlayGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" />
  </svg>
);

/** Miniatura 16:9 com play areia, fonte e duração. `onRemove` mostra "Remover vídeo" (hover no desktop, fixo no toque). */
export const TestimonialVideoThumb: React.FC<{
  video: TestimonialVideo;
  onPlay: () => void;
  onRemove?: () => void;
}> = ({ video, onPlay, onRemove }) => (
  <div
    role="button"
    tabIndex={0}
    aria-label="Assistir vídeo"
    onClick={(e) => {
      e.stopPropagation();
      onPlay();
    }}
    onKeyDown={(e) => e.key === 'Enter' && onPlay()}
    className="group/thumb relative aspect-video cursor-pointer overflow-hidden bg-sunken"
  >
    {video.posterUrl && (
      <img
        src={video.posterUrl}
        alt=""
        loading="lazy"
        className="absolute inset-0 size-full object-cover"
      />
    )}
    <span className="absolute inset-0 bg-[linear-gradient(180deg,color-mix(in_oklch,var(--sunken)_20%,transparent),color-mix(in_oklch,var(--sunken)_70%,transparent))]" />
    <span className="absolute left-1/2 top-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-sand text-on-sand shadow-[0_10px_30px_rgba(0,0,0,.5)] transition-transform duration-150 group-hover/thumb:scale-105">
      <PlayGlyph className="ml-[3px] size-5" />
    </span>
    <span className="col-label absolute bottom-3 left-3 flex h-6 items-center rounded-[7px] bg-sunken/80 px-[9px] tracking-[.12em] text-foreground backdrop-blur-md">
      {SOURCE[video.kind]}
    </span>
    {!!video.duration && (
      <span className="font-display absolute bottom-3 right-3 flex h-6 items-center rounded-[7px] bg-sunken/80 px-[9px] text-[13px] font-extrabold text-white backdrop-blur-md">
        {formatDuration(video.duration)}
      </span>
    )}
    {onRemove && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute right-2.5 top-2.5 flex h-8 items-center gap-1.5 rounded-[9px] border border-border bg-sunken/80 px-[11px] text-[11.5px] font-bold text-foreground backdrop-blur-md transition-[opacity,color,border-color] hover:border-destructive/50 hover:text-destructive md:opacity-0 md:group-hover/thumb:opacity-100 md:group-focus-within/thumb:opacity-100"
      >
        <Icon name="x" className="size-[13px]" />
        Remover vídeo
      </button>
    )}
  </div>
);

export const HomeBadge: React.FC = () => (
  <span className="flex h-[26px] items-center gap-1.5 rounded-full bg-sand px-2.5 text-[10px] font-extrabold uppercase tracking-[.12em] text-on-sand">
    <Icon name="house" className="size-3" strokeWidth={2.2} />
    No início
  </span>
);

export const SelectCheck: React.FC<{ selected: boolean }> = ({ selected }) => (
  <span
    className={cn(
      'flex size-[30px] items-center justify-center rounded-full border-[1.5px] text-on-sand',
      selected ? 'border-sand bg-sand' : 'border-ring',
    )}
  >
    {selected && <Icon name="check" className="size-[15px]" strokeWidth={2.6} />}
  </span>
);

export type TestimonialCardProps = {
  testimonial: Testimonial;
  /** Linhas antes do "Ler mais" (só aparece em textos > 300 caracteres). */
  clampLines?: number;
  onPlay?: () => void;
  /** Remover vídeo (só no admin). */
  onRemoveVideo?: () => void;
  /** Selos à direita das aspas — no admin, `<HomeBadge />`. */
  badges?: React.ReactNode;
  /** Modo seleção: o card inteiro vira alvo de clique. */
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Faixa de ações no rodapé — no admin, `<TestimonialActions />`. */
  actions?: React.ReactNode;
  now?: Date;
  className?: string;
};

export const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial: t,
  clampLines = 7,
  onPlay,
  onRemoveVideo,
  badges,
  selectable,
  selected,
  onSelect,
  actions,
  now,
  className,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const long = t.text.length > 300;
  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: vira role="button" (com aria-pressed) só no modo seleção.
    <article
      onClick={selectable ? onSelect : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              onSelect?.();
            }
          : undefined
      }
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? !!selected : undefined}
      className={cn(
        'anim-gl-fade flex flex-col overflow-hidden rounded-[18px] border border-border bg-card outline-2 outline-offset-2 transition-[border-color,outline-color] duration-150 hover:border-ring focus-visible:border-ring',
        selected ? 'outline outline-sand' : 'outline outline-transparent',
        selectable && 'cursor-pointer',
        className,
      )}
    >
      {t.video && (
        <TestimonialVideoThumb
          video={t.video}
          onPlay={() => (selectable ? onSelect?.() : onPlay?.())}
          onRemove={selectable ? undefined : onRemoveVideo}
        />
      )}
      <div className="flex flex-col gap-3.5 px-[22px] pb-[18px] pt-5">
        <div className="flex min-h-[30px] items-start gap-2.5">
          <span
            aria-hidden
            className="font-display h-[26px] text-[54px] font-extrabold leading-[.8] text-sand-dim/70"
          >
            “
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {badges}
            {selectable && <SelectCheck selected={!!selected} />}
          </span>
        </div>
        <p
          className="m-0 text-[15px] leading-[1.62] text-pretty text-foreground"
          style={
            long && !expanded
              ? {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: clampLines,
                  overflow: 'hidden',
                }
              : undefined
          }
        >
          {t.text}
        </p>
        {long && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((x) => !x);
            }}
            className="col-label -mt-1.5 self-start text-[10.5px] text-sand hover:text-sand/80"
          >
            {expanded ? 'Ler menos' : 'Ler mais'}
          </button>
        )}
        <div className="flex items-center gap-3 border-t border-border pt-3.5">
          <TestimonialAvatar name={t.name} photoUrl={t.photoUrl} />
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="truncate text-sm font-semibold text-white">{t.name}</span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <CampChip>{campLabel(t)}</CampChip>
              <span className="whitespace-nowrap text-xs text-faint">
                {relativeTime(t.createdAt, now)}
              </span>
            </span>
          </span>
        </div>
      </div>
      {actions}
    </article>
  );
};

/* ─────────────── Curadoria ─────────────── */

/** Interruptor em pílula — "Mostrar no início". Troca na hora; a página desfaz se a API falhar. */
export const HomeToggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
}> = ({ checked, onChange, disabled, label = 'Mostrar no início' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label="Mostrar no início"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={cn(
      'flex h-[38px] items-center gap-2.5 whitespace-nowrap rounded-full border pl-[9px] pr-[13px] text-[12.5px] font-bold transition-colors duration-150 hover:border-sand-dim disabled:opacity-40',
      checked
        ? 'border-sand-dim bg-sand/[.08] text-sand'
        : 'border-border text-muted-foreground',
    )}
  >
    <span
      className={cn(
        'relative h-5 w-8 rounded-full border transition-colors duration-150',
        checked ? 'border-sand bg-sand' : 'border-ring',
      )}
    >
      <span
        className={cn(
          'absolute left-0.5 top-0.5 size-3.5 rounded-full transition-transform duration-150 ease-[cubic-bezier(.2,.8,.2,1)]',
          checked ? 'translate-x-3 bg-on-sand' : 'bg-muted-foreground',
        )}
      />
    </span>
    {label}
  </button>
);

const VideoGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <Icon name="video" className={className} />
);

/** As três ações do card, e só elas. Fica sempre visível (celular não tem hover). */
export const TestimonialActions: React.FC<{
  showOnHome: boolean;
  hasVideo: boolean;
  onToggleHome: (v: boolean) => void;
  onAttach: () => void;
  onDelete: () => void;
}> = ({ showOnHome, hasVideo, onToggleHome, onAttach, onDelete }) => (
  <div className="flex flex-wrap items-center gap-2 border-t border-border bg-sunken px-3 py-2.5">
    <HomeToggle checked={showOnHome} onChange={onToggleHome} />
    <span className="ml-auto flex gap-1.5">
      <button
        type="button"
        onClick={onAttach}
        className="flex h-[38px] items-center gap-[7px] whitespace-nowrap rounded-full border border-border px-[13px] text-[12.5px] font-bold text-muted-foreground transition-colors hover:border-ring hover:text-white"
      >
        <VideoGlyph className="size-[15px]" />
        {hasVideo ? 'Trocar vídeo' : 'Anexar vídeo'}
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Excluir testemunho"
        title="Excluir testemunho"
        className="flex size-[38px] items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
      >
        <Icon name="trash-2" className="size-[15px]" />
      </button>
    </span>
  </div>
);

/* ─────────────── Topo, intro, filtros ─────────────── */

export const TestimonialsIntro: React.FC<{
  total: number;
  home: number;
  withVideo: number;
  limit?: number;
  loading?: boolean;
}> = ({ total, home, withVideo, limit = HOME_LIMIT, loading }) => {
  const v = (n: number) => (loading ? '—' : pad2(n));
  const cell = 'flex flex-col gap-1.5 px-3.5 py-3.5 md:px-5';
  return (
    <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
      <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-2">
        <span className="col-label flex items-center gap-2 tracking-[.18em] text-sand-dim">
          <span className="h-px w-[18px] bg-sand-dim" />
          Voz dos recrutas
        </span>
        <span className="font-display text-[clamp(26px,2.6vw,36px)] font-extrabold leading-[1.05] tracking-[-.03em] text-white">
          O que ficou da operação
        </span>
        <span className="max-w-[600px] text-sm text-pretty text-muted-foreground">
          Só os marcados como “No início” aparecem no site: todos em /testemunhos e os {limit}{' '}
          mais recentes na página inicial.
        </span>
      </div>
      <div className="grid grid-cols-[repeat(3,auto)] rounded-[14px] border border-border bg-card">
        <span className={cell}>
          <span className="col-label whitespace-nowrap text-muted-foreground">
            Testemunhos
          </span>
          <span className="font-display text-[26px] font-extrabold leading-none tracking-[-.03em] text-white">
            {v(total)}
          </span>
        </span>
        <span className={cn(cell, 'border-l border-border')}>
          <span className="col-label whitespace-nowrap text-muted-foreground">
            No início
          </span>
          <span className="font-display flex items-baseline gap-1 text-[26px] font-extrabold leading-none tracking-[-.03em] text-sand">
            {v(home)}
            <span className="text-sm tracking-normal text-faint">/ {limit}</span>
          </span>
        </span>
        <span className={cn(cell, 'border-l border-border')}>
          <span className="col-label whitespace-nowrap text-muted-foreground">
            Com vídeo
          </span>
          <span className="font-display text-[26px] font-extrabold leading-none tracking-[-.03em] text-white">
            {v(withVideo)}
          </span>
        </span>
      </div>
    </div>
  );
};

/** Aparece quando há mais marcados que a home mostra. */
export const HomeLimitNotice: React.FC<{
  count: number;
  limit?: number;
  onReview: () => void;
}> = ({ count, limit = HOME_LIMIT, onReview }) =>
  count > limit ? (
    <div className="anim-gl-fade flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[14px] border border-dashed border-sand-dim bg-sand/[.04] px-[18px] py-3.5">
      <Icon name="triangle-alert" className="size-[18px] shrink-0 text-sand" />
      <span className="flex-[1_1_280px] text-[13.5px] leading-normal text-pretty text-muted-foreground">
        A página inicial mostra até{' '}
        <strong className="text-sand">{limit} testemunhos</strong>. Há {count} marcados —
        entram os {limit} mais recentes; os outros ficam na reserva.
      </span>
      <button
        type="button"
        onClick={onReview}
        className="col-label shrink-0 text-[10.5px] text-sand hover:text-sand/80"
      >
        Revisar marcados
      </button>
    </div>
  ) : null;

export type TestimonialFilter = 'all' | 'home' | 'video' | 'novideo';
export const TESTIMONIAL_FILTERS: { id: TestimonialFilter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'home', label: 'No início' },
  { id: 'video', label: 'Com vídeo' },
  { id: 'novideo', label: 'Sem vídeo' },
];

export const matchesFilter = (t: Testimonial, f: TestimonialFilter) =>
  f === 'all' || (f === 'home' ? t.showOnHome : f === 'video' ? !!t.video : !t.video);

export const TestimonialFilters: React.FC<{
  query: string;
  onQuery: (q: string) => void;
  camp: string;
  camps: string[];
  onCamp: (c: string) => void;
  filter: TestimonialFilter;
  onFilter: (f: TestimonialFilter) => void;
  counts: Record<TestimonialFilter, number>;
  hint: string;
}> = ({ query, onQuery, camp, camps, onCamp, filter, onFilter, counts, hint }) => (
  <div className="flex flex-col gap-[22px]">
    <div className="flex flex-wrap gap-2.5">
      <SearchField
        icon={<Icon name="search" className="size-[18px]" />}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder="Buscar por nome ou trecho do texto..."
      />
      <SelectField
        label="Acampamento"
        value={camp}
        onChange={(e) => onCamp(e.target.value)}
        fieldClassName="flex-[1_1_260px] md:max-w-[340px]"
      >
        <option value="all">Todos os acampamentos</option>
        {camps.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </SelectField>
    </div>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex max-w-full gap-2 overflow-x-auto [scrollbar-width:none]">
        {TESTIMONIAL_FILTERS.map((f) => {
          const on = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => onFilter(f.id)}
              className={cn(
                'flex h-[38px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full border pl-[15px] pr-2 text-[13px] font-bold transition-colors duration-100',
                on
                  ? 'border-white bg-white text-background'
                  : 'border-border text-muted-foreground hover:border-ring',
              )}
            >
              {f.label}
              <span
                className={cn(
                  'font-display flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[11.5px] font-extrabold',
                  on ? 'bg-black/10' : 'bg-sand/[.08]',
                )}
              >
                {counts[f.id]}
              </span>
            </button>
          );
        })}
      </div>
      <span className="col-label ml-auto text-[10.5px] text-faint">{hint}</span>
    </div>
  </div>
);

/* ─────────────── Grade ─────────────── */

/**
 * Masonry em colunas com ordem de leitura preservada (esquerda → direita, mais recente primeiro).
 * Não usa CSS `columns` — lá a ordem desce pela 1ª coluna e o card pula de coluna ao expandir.
 * Distribui pelo menor acúmulo de altura ESTIMADA (texto limitado + vídeo), então "Ler mais" não reorganiza nada.
 */
export function TestimonialsMasonry<T extends Testimonial>({
  items,
  render,
  minColumn = 340,
  gap = 20,
}: {
  items: T[];
  render: (t: T) => React.ReactNode;
  minColumn?: number;
  gap?: number;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [cols, setCols] = React.useState(1);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(
      ([e]) =>
        e &&
        setCols(
          Math.max(
            1,
            Math.min(4, Math.floor((e.contentRect.width + gap) / (minColumn + gap))),
          ),
        ),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [minColumn, gap]);
  const columns = React.useMemo(() => {
    const acc = Array.from({ length: cols }, () => ({ h: 0, items: [] as T[] }));
    items.forEach((t) => {
      const target = acc.reduce((m, c) => (c.h < m.h - 40 ? c : m));
      target.items.push(t);
      target.h += 210 + Math.min(t.text.length, 300) * 0.28 + (t.video ? 200 : 0);
    });
    return acc;
  }, [items, cols]);
  return (
    <div ref={ref} className="flex items-start" style={{ gap }}>
      {columns.map((c, i) => (
        <div key={i} className="flex min-w-0 flex-1 flex-col" style={{ gap }}>
          {c.items.map((t) => (
            <React.Fragment key={t.id}>{render(t)}</React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

export const TestimonialsSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => {
  const shapes = [
    [92, 100, 84, 60],
    [100, 72],
    [96, 100, 100, 88, 40],
    [100, 90, 55],
    [88, 100, 64],
    [100, 76],
  ];
  return (
    <div
      className="grid gap-5"
      style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,340px),1fr))' }}
      role="status"
      aria-busy="true"
      aria-label="Carregando testemunhos"
    >
      {shapes.slice(0, count).map((lines, i) => (
        <div
          key={i}
          className="anim-tm-pulse flex flex-col gap-3 self-start rounded-[18px] border border-border bg-card p-[22px]"
          style={{ animationDelay: `${i * 0.12}s` }}
        >
          <span className="h-[22px] w-7 rounded-md bg-raised" />
          {lines.map((w, j) => (
            <span
              key={j}
              className="h-3 rounded-full bg-raised"
              style={{ width: `${w}%` }}
            />
          ))}
          <span className="mt-2 flex items-center gap-3">
            <span className="size-10 rounded-full bg-raised" />
            <span className="flex flex-1 flex-col gap-2">
              <span className="h-[11px] w-[55%] rounded-full bg-raised" />
              <span className="h-[9px] w-[35%] rounded-full bg-raised" />
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

export const TestimonialsEmpty: React.FC<{
  variant: 'all' | 'filter';
  query?: string;
  onClear?: () => void;
}> = ({ variant, query, onClear }) =>
  variant === 'all' ? (
    <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-card px-6 py-[72px] text-center">
      <span className="flex size-14 items-center justify-center rounded-full border border-dashed border-ring text-muted-foreground">
        <Icon name="message-square-quote" className="size-[22px]" />
      </span>
      <span className="font-display text-[17px] font-extrabold uppercase tracking-[-.01em] text-white">
        Nenhum testemunho ainda
      </span>
      <span className="max-w-[400px] text-[13px] leading-[1.55] text-pretty text-muted-foreground">
        Eles chegam pelo site, em{' '}
        <strong className="font-semibold text-sand">/testemunhos</strong>. Assim que
        alguém enviar, o testemunho aparece aqui para curadoria.
      </span>
    </div>
  ) : (
    <div className="flex flex-col items-center gap-2.5 rounded-[18px] border border-dashed border-ring px-6 py-14 text-center">
      <span className="font-display text-base font-extrabold uppercase tracking-[-.01em] text-white">
        Nada neste filtro
      </span>
      <span className="max-w-[380px] text-[13px] text-pretty text-muted-foreground">
        {query
          ? `Nenhum testemunho com “${query}” no nome ou no texto.`
          : 'Nenhum testemunho combina com os filtros escolhidos.'}
      </span>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="mt-1.5 flex h-10 items-center rounded-full border border-border px-[18px] text-[13px] font-bold text-muted-foreground hover:border-ring hover:text-white"
        >
          Limpar filtros
        </button>
      )}
    </div>
  );

/* ─────────────── Toast de curadoria ─────────────── */

export type CurationToast = { kind: 'ok' | 'error'; title: string; body: string } | null;

/** Toast próprio da tela (canto superior direito, sob o topo). Use o `toaster` global se preferir — o texto é o mesmo. */
export const CurationToastView: React.FC<{
  toast: CurationToast;
  onClose: () => void;
}> = ({ toast, onClose }) => {
  React.useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onClose, 4200);
    return () => clearTimeout(id);
  }, [toast, onClose]);
  if (!toast) return null;
  const err = toast.kind === 'error';
  return (
    <div
      role="status"
      className={cn(
        'anim-ci-in fixed right-4 top-[88px] z-[70] flex w-[380px] max-w-[calc(100vw-32px)] items-start gap-3 rounded-[14px] border bg-card px-4 py-3.5 shadow-[0_20px_50px_rgba(0,0,0,.7)]',
        err ? 'border-destructive/50' : 'border-ring',
      )}
    >
      <Icon
        name={err ? 'triangle-alert' : 'circle-check'}
        className={cn(
          'mt-px size-[18px] shrink-0',
          err ? 'text-destructive' : 'text-olive-icon',
        )}
      />
      <span className="flex flex-1 flex-col gap-[3px]">
        <span className="text-[13.5px] font-semibold text-white">{toast.title}</span>
        <span className="text-[12.5px] leading-[1.45] text-muted-foreground">
          {toast.body}
        </span>
      </span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar"
        className="shrink-0 text-faint hover:text-white"
      >
        <Icon name="x" className="size-[15px]" />
      </button>
    </div>
  );
};

/* ─────────────── Anexar vídeo ─────────────── */

export type AttachVideoResult = TestimonialVideo;

export const AttachVideoDialog: React.FC<{
  open: boolean;
  testimonial: Testimonial | null;
  /** Sobe o arquivo e devolve a URL final. Chame `onProgress(0..100)` durante o envio. */
  upload: (
    file: File,
    onProgress: (pct: number) => void,
  ) => Promise<{ url: string; posterUrl?: string | null }>;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (video: AttachVideoResult) => void;
}> = ({ open, testimonial: t, upload, loading, onClose, onSubmit }) => {
  const [tab, setTab] = React.useState<'file' | 'link'>('file');
  const [file, setFile] = React.useState<{
    name: string;
    size: number;
    preview: string;
    duration?: number;
    progress: number;
    url?: string;
    posterUrl?: string | null;
    error?: boolean;
  } | null>(null);
  const [link, setLink] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const kind = t?.video?.kind;
    setTab(kind && kind !== 'file' ? 'link' : 'file');
    setLink(kind && kind !== 'file' ? t!.video!.url : '');
    setFile(null);
  }, [open, t]);

  const parsed = parseVideoLink(link);
  const ready = tab === 'file' ? !!file?.url : !!parsed && parsed.kind !== 'invalid';

  const submit = React.useCallback(() => {
    if (!ready || loading) return;
    if (tab === 'file')
      onSubmit({
        kind: 'file',
        url: file!.url!,
        posterUrl: file!.posterUrl ?? null,
        duration: file!.duration ?? null,
      });
    else
      onSubmit({
        kind: parsed!.kind as 'youtube' | 'instagram',
        url: link.trim(),
        posterUrl: (parsed as any).posterUrl ?? null,
        duration: null,
      });
  }, [ready, loading, tab, file, parsed, link, onSubmit]);

  React.useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [open, onClose, submit]);

  const start = (f: File) => {
    if (!/video\/(mp4|quicktime)/.test(f.type) && !/\.(mp4|mov)$/i.test(f.name)) return;
    const preview = URL.createObjectURL(f);
    setFile({ name: f.name, size: f.size, preview, progress: 0 });
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.src = preview;
    v.onloadedmetadata = () =>
      setFile((x) => (x && x.preview === preview ? { ...x, duration: v.duration } : x));
    upload(f, (pct) =>
      setFile((x) => (x && x.preview === preview ? { ...x, progress: pct } : x)),
    )
      .then((r) =>
        setFile((x) =>
          x && x.preview === preview
            ? { ...x, progress: 100, url: r.url, posterUrl: r.posterUrl }
            : x,
        ),
      )
      .catch(() =>
        setFile((x) => (x && x.preview === preview ? { ...x, error: true } : x)),
      );
  };

  if (!open || !t) return null;
  const done = !!file?.url;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-[rgba(3,4,2,.78)] p-4 backdrop-blur-[6px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        onDragOver={(e) => tab === 'file' && e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f && tab === 'file') start(f);
        }}
        className="anim-ci-in m-auto w-full max-w-[520px] rounded-3xl border border-border bg-card shadow-[0_30px_80px_rgba(0,0,0,.7)]"
      >
        <div className="flex flex-col gap-1.5 px-[26px] pt-6">
          <span className="col-label tracking-[.16em] text-sand-dim">
            {t.video ? 'Trocar vídeo' : 'Anexar vídeo'}
          </span>
          <span className="font-display text-2xl font-extrabold uppercase leading-[1.05] tracking-[-.025em] text-white">
            Vídeo do testemunho
          </span>
        </div>
        <div className="flex flex-col gap-4 px-[26px] pb-[26px] pt-[18px]">
          <div className="flex gap-3 rounded-[14px] border border-border bg-sunken p-3.5">
            <TestimonialAvatar name={t.name} photoUrl={t.photoUrl} />
            <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <span className="text-sm font-semibold text-white">{t.name}</span>
                <span className="col-label tracking-[.1em] text-muted-foreground">
                  {campLabel(t)}
                </span>
              </span>
              <span className="line-clamp-2 text-[13px] leading-normal text-muted-foreground">
                “{t.text}”
              </span>
            </span>
          </div>

          <div
            className="flex rounded-full border border-border bg-sunken p-1"
            role="tablist"
          >
            {(['file', 'link'] as const).map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  'flex h-[38px] flex-1 items-center justify-center rounded-full text-[13px] font-bold transition-colors duration-100',
                  tab === id
                    ? 'bg-white text-background'
                    : 'text-muted-foreground hover:text-white',
                )}
              >
                {id === 'file' ? 'Enviar arquivo' : 'Colar link'}
              </button>
            ))}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,.mp4,.mov"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) start(f);
              e.target.value = '';
            }}
          />

          {tab === 'file' && !file && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex h-[190px] flex-col items-center justify-center gap-2.5 rounded-2xl border border-dashed border-ring bg-sunken p-4 text-center transition-colors hover:border-sand-dim hover:bg-sand/[.03]"
            >
              <span className="flex size-12 items-center justify-center rounded-full border border-olive-line/40 bg-accent text-olive-icon">
                <Icon name="upload" className="size-5" />
              </span>
              <span className="text-sm font-semibold text-white">
                Arraste o vídeo aqui ou clique para escolher
              </span>
              <span className="text-[12.5px] text-muted-foreground">
                MP4 ou MOV • até 500 MB • até 5 minutos
              </span>
            </button>
          )}

          {tab === 'file' && file && (
            <div className="flex flex-col gap-3">
              <div className="relative aspect-video overflow-hidden rounded-[14px] border border-border bg-black">
                {/* biome-ignore lint/a11y/useMediaCaption: prévia local do arquivo escolhido, antes de anexar. */}
                <video
                  src={file.preview}
                  poster={file.posterUrl ?? undefined}
                  controls
                  className="absolute inset-0 size-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-2.5">
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-white">
                    {file.name}
                  </span>
                  <span className="shrink-0 text-xs text-faint">
                    {(file.size / 1048576).toFixed(1).replace('.', ',')} MB
                  </span>
                </span>
                <span className="h-1.5 overflow-hidden rounded-full bg-sand/[.12]">
                  <span
                    className={cn(
                      'block h-full rounded-full transition-[width] duration-200',
                      file.error ? 'bg-destructive' : 'bg-sand',
                    )}
                    style={{ width: `${Math.round(file.progress)}%` }}
                  />
                </span>
                <span className="flex items-center gap-2.5">
                  <span
                    className={cn(
                      'col-label text-[10.5px]',
                      file.error
                        ? 'text-destructive'
                        : done
                          ? 'text-olive-ink'
                          : 'text-sand',
                    )}
                  >
                    {file.error
                      ? 'Falha no envio'
                      : done
                        ? 'Pronto para anexar'
                        : `Enviando ${Math.round(file.progress)}%`}
                  </span>
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="col-label ml-auto text-[10.5px] text-muted-foreground hover:text-sand"
                  >
                    Trocar arquivo
                  </button>
                </span>
              </div>
            </div>
          )}

          {tab === 'link' && (
            <div className="flex flex-col gap-3">
              <Field
                label="Link do vídeo"
                required
                autoFocus
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Ex: https://youtu.be/…"
                leading={<Icon name="link" className="size-4 text-faint" />}
                fieldClassName={cn(parsed?.kind === 'invalid' && 'border-destructive/60')}
              />
              <span
                className={cn(
                  'text-xs',
                  parsed?.kind === 'invalid' ? 'text-destructive' : 'text-faint',
                )}
              >
                {parsed?.kind === 'invalid'
                  ? 'Link não reconhecido. Use um endereço do YouTube ou do Instagram.'
                  : 'YouTube (vídeo ou Shorts) ou Instagram (Reels ou publicação).'}
              </span>
              {parsed && parsed.kind !== 'invalid' && (
                <div className="anim-gl-fade relative aspect-video overflow-hidden rounded-[14px] border border-border bg-sunken">
                  {parsed.posterUrl ? (
                    <img
                      src={parsed.posterUrl}
                      alt=""
                      className="absolute inset-0 size-full object-cover"
                    />
                  ) : (
                    <span className="absolute inset-3.5 flex items-center justify-center rounded-[10px] border border-dashed border-ring p-4 text-center text-[12.5px] leading-normal text-muted-foreground">
                      Sem miniatura para Instagram. O vídeo é incorporado no site como
                      publicação.
                    </span>
                  )}
                  <span className="absolute left-2.5 top-2.5 flex h-6 items-center rounded-[7px] bg-sand px-[9px] text-[10px] font-extrabold uppercase tracking-[.12em] text-on-sand">
                    {SOURCE[parsed.kind]}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mt-0.5 flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex h-[50px] flex-[1_1_140px] items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground hover:border-ring hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!ready || loading}
              className="flex h-[50px] flex-[2_1_220px] items-center justify-center rounded-full bg-sand text-sm font-bold text-on-sand transition-transform hover:bg-sand/90 active:scale-[.97] disabled:opacity-40"
            >
              {loading ? 'Salvando…' : t.video ? 'Trocar vídeo' : 'Anexar vídeo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Player ─────────────── */

const embedUrl = (v: TestimonialVideo) => {
  if (v.kind === 'youtube') {
    const p = parseVideoLink(v.url);
    return p && p.kind === 'youtube'
      ? `https://www.youtube.com/embed/${p.id}?autoplay=1&rel=0`
      : null;
  }
  if (v.kind === 'instagram') return v.url.replace(/\/?(\?.*)?$/, '/embed');
  return null;
};

/** Tela cheia no padrão do PhotoLightbox: fundo 96% sem blur, fechar 40px, Esc. `paused` quando houver dialog por cima. */
export const VideoPlayer: React.FC<{
  testimonial: Testimonial | null;
  onClose: () => void;
  paused?: boolean;
}> = ({ testimonial: t, onClose, paused }) => {
  const open = !!t?.video;
  React.useEffect(() => {
    if (!open || paused) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, paused, onClose]);
  if (!open) return null;
  const v = t!.video!;
  const embed = embedUrl(v);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Vídeo de ${t!.name}`}
      className="anim-gl-fade fixed inset-0 z-50 flex flex-col bg-[rgba(3,4,2,.96)]"
    >
      <div className="flex shrink-0 items-center gap-4 px-4 py-4 md:px-7">
        <TestimonialAvatar name={t!.name} photoUrl={t!.photoUrl} />
        <span className="mr-auto flex min-w-0 flex-col gap-1">
          <span className="truncate text-sm font-semibold text-white">{t!.name}</span>
          <span className="col-label text-[10.5px] text-sand">{campLabel(t!)}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar (Esc)"
          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border text-foreground hover:border-ring hover:text-sand"
        >
          <Icon name="x" className="size-[18px]" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 md:px-7">
        <div className="anim-ci-in relative aspect-video max-h-full w-full max-w-[1080px] overflow-hidden rounded-[14px] bg-black">
          {v.kind === 'file' ? (
            // biome-ignore lint/a11y/useMediaCaption: o vídeo chega pelo painel sem arquivo de legendas.
            <video
              src={v.url}
              poster={v.posterUrl ?? undefined}
              controls
              autoPlay
              className="absolute inset-0 size-full object-contain"
            />
          ) : embed ? (
            <iframe
              src={embed}
              title={`Vídeo de ${t!.name}`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 size-full border-0"
            />
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 justify-center px-4 pb-[26px] pt-[18px] md:px-7">
        <span className="line-clamp-2 max-w-[760px] text-center text-sm leading-relaxed text-pretty text-muted-foreground">
          “{t!.text}”
        </span>
      </div>
    </div>
  );
};

export const selectionLabel = (n: number) => plural(n, 'selecionado', 'selecionados');
